import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';

/**
 * Componente AudioPlayer para reproduzir áudios no chat
 * @param {Object} props - Propriedades do componente
 * @param {string} props.audioUrl - URL ou base64 do áudio
 * @param {string} [props.caption] - Legenda do áudio
 * @param {boolean} [props.isFromMe] - Se o áudio foi enviado por mim
 * @param {boolean} [props.isAI] - Se o áudio foi enviado pela IA
 * @param {string} [props.duration] - Duração do áudio em segundos
 * @returns {JSX.Element} Componente de player de áudio
 */
export const AudioPlayer = ({
  audioUrl,
  caption,
  isFromMe = false,
  isAI = false,
  duration: providedDuration
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(providedDuration || 0);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

    // Cores baseadas no tipo de mensagem (classes fixas para Tailwind)
  const getColorClasses = () => {
    if (isAI) { // White colors for AI audios using tailwind.config.js
      return {
        primaryBg: 'bg-whiteBase',
        primaryHover: 'hover:bg-graySubtle',
        primaryIcon: 'text-whiteBase',
        primaryIconBg: 'bg-graySubtle',
        progressBg: 'bg-graySubtle',
        progressFill: 'bg-grayLight',
        containerBg: 'bg-whiteBase/10',
        containerBorder: 'border-whiteBase/60'
      };
    }
    if (isFromMe) { // Green colors for user audios
      return {
        primaryBg: 'bg-emeraldBase',
        primaryHover: 'hover:bg-emeraldBase/80',
        primaryIcon: 'text-emeraldBase',
        primaryIconBg: 'bg-emeraldBase/20',
        progressBg: 'bg-emeraldBase/20',
        progressFill: 'bg-emeraldBase',
        containerBg: 'bg-emeraldBase/10',
        containerBorder: 'border-emeraldBase/60'
      };
    }
    return { // Orange colors for received audios
      primaryBg: 'bg-statusWarning',
      primaryHover: 'hover:bg-statusWarning/80',
      primaryIcon: 'text-statusWarning',
      primaryIconBg: 'bg-statusWarning/20',
      progressBg: 'bg-statusWarning/20',
      progressFill: 'bg-statusWarning',
      containerBg: 'bg-statusWarning/10',
      containerBorder: 'border-statusWarning/60'
    };
  };

  const colorClasses = getColorClasses();

  // Inicializa o áudio quando o componente é montado
  useEffect(() => {
    if (!audioUrl) return;

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
    };

    const handleError = () => {
      setError('Erro ao carregar áudio');
      setIsLoading(false);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.pause();
    };
  }, [audioUrl]);

  // Função para alternar play/pause
  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Função para navegar no áudio
  const handleProgressClick = (e) => {
    if (!audioRef.current || !progressBarRef.current) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newProgress = (clickX / rect.width) * 100;
    const newTime = (newProgress / 100) * duration;

    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress(newProgress);
  };

  // Função para formatar tempo
  const formatTime = (timeInSeconds) => {
    if (!timeInSeconds || isNaN(timeInSeconds)) return '0:00';

    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };



  if (error) {
    return (
      <div className="flex items-center p-2.5 rounded-xl bg-redBase/10 border border-redBase/60 space-x-3">
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-redBase/20 flex-shrink-0">
          <Volume2 className="h-5 w-5 text-redBase" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-redBase">Erro no áudio</span>
          <div className="text-xs text-redBase/70">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center p-2.5 rounded-xl ${colorClasses.containerBg} border ${colorClasses.containerBorder} space-x-3 min-w-0`}>
      {/* Botão Play/Pause */}
      <button
        onClick={togglePlayPause}
        disabled={isLoading || error}
        className={`flex items-center justify-center w-12 h-12 rounded-full ${colorClasses.primaryBg} ${colorClasses.primaryHover} transition-colors duration-200 disabled:opacity-50 flex-shrink-0 shadow-sm`}
      >
        {isLoading ? (
          <div className={`w-4 h-4 border-2 ${isAI ? 'border-grayLight' : 'border-whiteBase'} border-t-transparent rounded-full animate-spin`} />
        ) : isPlaying ? (
          <Pause className={`h-5 w-5 ${isAI ? 'text-grayLight' : 'text-whiteBase'}`} />
        ) : (
          <Play className={`h-5 w-5 ${isAI ? 'text-grayLight' : 'text-whiteBase'} ml-0.5`} />
        )}
      </button>

      {/* Conteúdo do áudio */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Tipo de áudio */}
        <div className="flex items-center space-x-1.5">
          <Volume2 className={`h-3.5 w-3.5 ${colorClasses.primaryIcon} flex-shrink-0`} />
          <span className={`text-sm font-medium ${colorClasses.primaryIcon} truncate`}>
            {isAI ? 'Áudio da IA' : isFromMe ? 'Seu áudio' : 'Áudio recebido'}
          </span>
        </div>

        {/* Barra de progresso */}
        <div
          ref={progressBarRef}
          onClick={handleProgressClick}
          className={`h-1 ${colorClasses.progressBg} rounded-full cursor-pointer relative overflow-hidden`}
        >
          <div
            className={`h-full ${colorClasses.progressFill} rounded-full transition-all duration-150`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Caption se existir */}
        {caption && (
          <div className="text-xs text-grayText/80 truncate">
            {caption}
          </div>
        )}
      </div>

      {/* Tempo */}
      <div className="text-sm text-grayText/90 font-mono flex-shrink-0 min-w-[2.5rem] text-right">
        {formatTime(isPlaying ? currentTime : duration)}
      </div>
    </div>
  );
};

export default AudioPlayer;
